import { Building2, ChevronLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";

import { CardBrandingExplainer, type CardBrandingStatus } from "../components/branding/CardBrandingExplainer";
import { CardDesignPanel } from "../components/branding/CardDesignPanel";
import { CardPreviewVisual } from "../components/CardPreviewVisual";
import { CARD_GRADIENTS, DEFAULT_GRADIENT_ID } from "../constants/cardGradients";
import { useOrgBranding } from "../hooks/useOrgBranding";
import type { CardStylesConfig } from "../types/branding.types";

const COLOCA_DESIGN: CardStylesConfig = {
  gradient_id: DEFAULT_GRADIENT_ID,
  secondary_logo_url: null,
  secondary_logo_scale: 1,
};

export const BrandingPage = () => {
  const { t } = useTranslations("cards");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, isInternalUser } = useAuth();

  // Internal users reach this page via `?org_id=<uuid>` from a card detail.
  const orgIdFromQuery = params.get("org_id");
  const resolvedOrgId =
    (orgIdFromQuery && orgIdFromQuery.trim().length > 0 ? orgIdFromQuery : user?.organization?.id) ?? null;

  const branding = useOrgBranding(resolvedOrgId);

  const [draft, setDraft] = useState<CardStylesConfig>({
    gradient_id: DEFAULT_GRADIENT_ID,
    secondary_logo_url: null,
    secondary_logo_scale: 1,
  });

  // Re-sync only when nothing's in flight, otherwise we'd clobber unsaved
  // tweaks. Without this, the first render captures EMPTY_DESIGN.
  useEffect(() => {
    if (branding.isLoading || branding.isSaving || branding.isUploadingLogo) {
      return;
    }
    // eslint-disable-next-line react-hooks-eslint/set-state-in-effect
    setDraft({
      gradient_id: branding.design.gradient_id ?? DEFAULT_GRADIENT_ID,
      secondary_logo_url: branding.design.secondary_logo_url ?? null,
      secondary_logo_scale: branding.design.secondary_logo_scale ?? 1,
    });
  }, [
    branding.isLoading,
    branding.isSaving,
    branding.isUploadingLogo,
    branding.design.gradient_id,
    branding.design.secondary_logo_url,
    branding.design.secondary_logo_scale,
  ]);

  // TODO: org picker for internal users without a personal org.
  const isInternalWithoutOrg = isInternalUser && !resolvedOrgId;
  if (!resolvedOrgId && !isInternalUser) {
    return (
      <div className="mx-auto flex max-w-xl flex-col items-center gap-4 p-12 text-center">
        <Building2 size={36} className="text-muted-foreground" />
        <h2 className="text-xl font-semibold text-foreground">{t("branding_no_org_title")}</h2>
        <p className="text-sm text-muted-foreground">{t("branding_no_org_description")}</p>
        <ColocaButton variant="default" onClick={() => navigate("/app/cards/cards")}>
          {t("branding_no_org_cta")}
        </ColocaButton>
      </div>
    );
  }

  const effectiveCanCustomize = isInternalWithoutOrg ? true : branding.canCustomize;
  // Staff switch it from Organizations; a member reads whether Coloca turned it on for its organization.
  const brandingStatus: CardBrandingStatus = isInternalUser
    ? "staff"
    : effectiveCanCustomize
      ? "enabled"
      : "locked";
  // Switched off, the secure view shows the Coloca design (the saved one waits), so the preview does too.
  const previewDesign: CardStylesConfig = effectiveCanCustomize ? draft : COLOCA_DESIGN;
  const previewGradientName =
    CARD_GRADIENTS.find((g) => g.id === (previewDesign.gradient_id ?? DEFAULT_GRADIENT_ID))?.name ?? "—";

  return (
    <div className="min-h-full">
      {/* Negative margins cancel ColocaAppShell's `p-4` so the topbar spans
          the full viewport width. */}
      <div className="-mx-4 -mt-4 mb-4 flex h-16 items-center justify-between gap-4 border-b border-border bg-card px-6">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/app/cards/cards")}
            className="inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-sm text-muted-foreground hover:bg-muted"
          >
            <ChevronLeft size={16} /> {t("cards_header_title_cards")}
          </button>
          <span className="text-muted-foreground/50">/</span>
          <div className="min-w-0">
            <div className="truncate text-base font-semibold text-foreground">{t("branding_page_title")}</div>
            <div className="text-xs text-muted-foreground">{t("branding_page_subtitle")}</div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1200px] space-y-6 p-6">
        <CardBrandingExplainer status={brandingStatus} />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[360px_1fr]">
          <div className="space-y-4">
            <div className="flex justify-center rounded-2xl border border-border bg-gradient-to-b from-muted to-transparent p-6">
              <CardPreviewVisual
                alias={t("branding_preview_alias")}
                last4="0000"
                type="VIRTUAL"
                exp="12/30"
                holder={t("branding_preview_holder")}
                size="lg"
                design={previewDesign}
                iframeLike
              />
            </div>
            <div className="space-y-3 rounded-2xl border border-border bg-card p-4">
              <div className="text-sm font-medium text-foreground">{t("branding_summary_title")}</div>
              <div className="space-y-2.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t("branding_summary_label_gradient")}</span>
                  <span className="font-medium text-foreground">{previewGradientName}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">{t("branding_summary_label_logo")}</span>
                  <span className="font-medium text-foreground">
                    {previewDesign.secondary_logo_url
                      ? t("branding_logo_custom")
                      : t("branding_logo_default_coloca")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="min-w-0">
            {branding.isLoading ? (
              <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
                {t("branding_loading")}
              </div>
            ) : (
              <CardDesignPanel
                design={branding.design}
                canCustomize={effectiveCanCustomize}
                isSaving={branding.isSaving}
                isUploadingLogo={branding.isUploadingLogo}
                onDraftChange={setDraft}
                onSave={branding.save}
                onReset={branding.reset}
                onUploadLogo={branding.uploadLogo}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandingPage;
